import type { Map as MapboxMap } from 'mapbox-gl';
import { useEffect, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';

/**
 * Adds an image drawn by `draw` under `imageId` once per style. Returns the id once the map holds
 * it, null before, so a symbol layer never references a missing image.
 */
export function useMapImage(
  imageId: string | null,
  draw: (map: MapboxMap, imageId: string) => void,
): string | null {
  const { current: map } = useMap();
  const [readyImageId, setReadyImageId] = useState<string | null>(null);

  useEffect(() => {
    if (map == null || imageId == null) return;
    const mapbox = map.getMap();
    // addImage fires `styledata`, and BasicMap reads the style on it, which throws until the style
    // has loaded. A basemap switch loads a new style without the image, so this re-adds it.
    const addWhenLoaded = () => {
      if (!mapbox.isStyleLoaded()) return;
      if (!mapbox.hasImage(imageId)) draw(mapbox, imageId);
      setReadyImageId(imageId);
    };
    addWhenLoaded();
    mapbox.on('styledata', addWhenLoaded);
    mapbox.on('idle', addWhenLoaded);
    return () => {
      mapbox.off('styledata', addWhenLoaded);
      mapbox.off('idle', addWhenLoaded);
    };
  }, [map, imageId]);

  return readyImageId === imageId ? imageId : null;
}
